/**
 * Barra de filtros compartida de las gráficas del Panorama (V-21).
 *
 * P0 visible: fabricante, proveedor, modelos, rango de rendimiento y habilidad.
 * P1 en «Más filtros»: precio, contexto, capacidades, lifecycle, cobertura,
 * frescura, open weights/licencia, corte de conocimiento y Elo/precio.
 *
 * No calcula datos: recibe opciones derivadas del catálogo y notifica cambios
 * (la página los serializa en la URL).
 */

import { useMemo, useState } from "react";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui";
import { useI18n } from "@/i18n";
import type { SkillKey } from "../lib/panorama";
import {
  countActiveFilters,
  PANORAMA_CAPABILITIES,
  type PanoramaCapability,
  type PanoramaFilterOption,
  type PanoramaFilterOptions,
  type PanoramaFilters,
  type PanoramaRangeCounts,
} from "../lib/panoramaFilters";

interface PanoramaFiltersProps {
  filters: PanoramaFilters;
  options: PanoramaFilterOptions;
  rangeCounts: PanoramaRangeCounts;
  matchedModels: number;
  totalModels: number;
  priceVisible: boolean;
  onChange: (next: PanoramaFilters) => void;
  onReset: () => void;
}

function MultiSelect({
  label,
  testId,
  options,
  value,
  onChange,
  size = 4,
}: {
  label: string;
  testId: string;
  options: PanoramaFilterOption[];
  value: string[];
  onChange: (next: string[]) => void;
  size?: number;
}) {
  return (
    <label className="space-y-1 text-xs text-muted-foreground">
      <span className="block font-medium text-foreground">{label}</span>
      <select
        multiple
        size={size}
        value={value}
        data-testid={testId}
        onChange={(event) =>
          onChange(Array.from(event.target.selectedOptions).map((option) => option.value))
        }
        className="w-full rounded border bg-background px-2 py-1 text-xs text-foreground"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function NumberField({
  label,
  testId,
  value,
  onChange,
  disabled = false,
  placeholder,
}: {
  label: string;
  testId: string;
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="space-y-1 text-xs text-muted-foreground">
      <span className="block font-medium text-foreground">{label}</span>
      <input
        type="number"
        min={0}
        step="any"
        value={value ?? ""}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testId}
        onChange={(event) =>
          onChange(event.target.value === "" ? null : Number(event.target.value))
        }
        className="w-full rounded border bg-background px-2 py-1 text-xs text-foreground disabled:opacity-50"
      />
    </label>
  );
}

export default function PanoramaFilters({
  filters,
  options,
  rangeCounts,
  matchedModels,
  totalModels,
  priceVisible,
  onChange,
  onReset,
}: PanoramaFiltersProps) {
  const { t } = useI18n();
  const [modelQuery, setModelQuery] = useState("");
  const [open, setOpen] = useState(false);
  const activeCount = countActiveFilters(filters);

  const models = useMemo(() => {
    const query = modelQuery.trim().toLowerCase();
    const selected = new Set(filters.modelRefs);
    return options.models.filter(
      (option) =>
        selected.has(option.value) ||
        !query ||
        option.value.toLowerCase().includes(query) ||
        option.label.toLowerCase().includes(query),
    );
  }, [filters.modelRefs, modelQuery, options.models]);

  const update = (patch: Partial<PanoramaFilters>) =>
    onChange({ ...filters, ...patch });

  const toggleList = <T extends string>(current: T[], value: T): T[] =>
    current.includes(value)
      ? current.filter((entry) => entry !== value)
      : [...current, value];

  const capabilityLabel = (value: PanoramaCapability) =>
    t(
      `modelIntel.panorama.filters.cap.${
        value === "structured_output" ? "structured" : value
      }`,
    );

  const lifecycleLabel = (value: string) =>
    t(
      `modelIntel.panorama.filters.lifecycle${
        value === "ga"
          ? "Ga"
          : value === "stable"
            ? "Ga"
            : value === "preview"
              ? "Preview"
              : value === "deprecated"
                ? "Deprecated"
                : value === "retired"
                  ? "Retired"
                  : "Unknown"
      }`,
    );

  const skillLabel = (value: "general" | SkillKey) =>
    value === "general"
      ? t("modelIntel.panorama.filters.skillGeneral")
      : t(
          `modelIntel.panorama.skills.${
            value === "codigo"
              ? "code"
              : value === "conversacion"
                ? "conversation"
                : "reasoning"
          }`,
        );

  return (
    <section
      className="rounded-lg border bg-card"
      data-testid="panorama-chart-filters"
      aria-label={t("modelIntel.panorama.filters.title")}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls="panorama-filters-panel"
          title={t("modelIntel.panorama.filters.subtitle")}
          data-testid="panorama-filters-toggle"
        >
          <SlidersHorizontal className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="shrink-0 text-sm font-medium">
            {t("modelIntel.panorama.filters.title")}
          </span>
          <span
            className="truncate text-xs text-muted-foreground"
            data-testid="panorama-filters-result"
          >
            {t("modelIntel.panorama.filters.results", {
              shown: matchedModels,
              total: totalModels,
            })}
          </span>
          {activeCount > 0 && (
            <span
              className="shrink-0 whitespace-nowrap text-xs text-muted-foreground"
              data-testid="panorama-filters-active"
            >
              {t("modelIntel.panorama.filters.active", { count: activeCount })}
            </span>
          )}
          <ChevronDown
            className={`ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          />
        </button>
        {activeCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onReset}
            data-testid="panorama-filters-reset"
          >
            {t("modelIntel.panorama.filters.reset")}
          </Button>
        )}
      </div>

      {open && (
        <div
          id="panorama-filters-panel"
          className="space-y-3 border-t p-3"
          data-testid="panorama-filters-panel"
        >
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <div className="space-y-2 xl:col-span-2">
          <label className="space-y-1 text-xs text-muted-foreground">
            <span className="block font-medium text-foreground">
              {t("modelIntel.panorama.filters.searchModel")}
            </span>
            <input
              type="search"
              value={modelQuery}
              placeholder={t("modelIntel.panorama.filters.modelPlaceholder")}
              data-testid="panorama-filter-model-search"
              onChange={(event) => setModelQuery(event.target.value)}
              className="w-full rounded border bg-background px-2 py-1 text-xs text-foreground"
            />
          </label>
          <MultiSelect
            label={t("modelIntel.panorama.filters.models")}
            testId="panorama-filter-models"
            options={models}
            value={filters.modelRefs}
            onChange={(next) => update({ modelRefs: next })}
            size={4}
          />
        </div>
        <MultiSelect
          label={t("modelIntel.panorama.filters.publishers")}
          testId="panorama-filter-publishers"
          options={options.publishers}
          value={filters.publishers}
          onChange={(next) => update({ publishers: next })}
        />
        <MultiSelect
          label={t("modelIntel.panorama.filters.providers")}
          testId="panorama-filter-providers"
          options={options.providers}
          value={filters.providers}
          onChange={(next) => update({ providers: next })}
        />
        <label className="space-y-1 text-xs text-muted-foreground">
          <span className="block font-medium text-foreground">
            {t("modelIntel.panorama.filters.skill")}
          </span>
          <select
            value={filters.skill}
            data-testid="panorama-filter-skill"
            onChange={(event) =>
              update({ skill: event.target.value as PanoramaFilters["skill"] })
            }
            className="w-full rounded border bg-background px-2 py-1 text-xs text-foreground"
          >
            <option value="general">{skillLabel("general")}</option>
            {(["codigo", "conversacion", "razonamiento"] as const).map((skill) => (
              <option key={skill} value={skill}>
                {skillLabel(skill)}
              </option>
            ))}
          </select>
          <div className="text-[11px] text-muted-foreground">
            {t("modelIntel.panorama.filters.rangeHint", {
              top: rangeCounts.top,
              mid: rangeCounts.mid,
              base: rangeCounts.base,
            })}
          </div>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="panorama-filter-range">
        <span className="text-xs font-medium">{t("modelIntel.panorama.filters.range")}</span>
        {(
          [
            ["all", rangeCounts.all],
            ["top", rangeCounts.top],
            ["mid", rangeCounts.mid],
            ["base", rangeCounts.base],
          ] as const
        ).map(([range, count]) => (
          <Button
            key={range}
            type="button"
            size="sm"
            variant={filters.range === range ? "default" : "outline"}
            aria-pressed={filters.range === range}
            data-testid={`panorama-filter-range-${range}`}
            onClick={() => update({ range })}
          >
            {t(
              `modelIntel.panorama.filters.range${
                range === "all"
                  ? "All"
                  : range === "top"
                    ? "Top"
                    : range === "mid"
                      ? "Mid"
                      : "Base"
              }`,
            )}{" "}
            ({count})
          </Button>
        ))}
      </div>

      <details className="rounded border px-3 py-2" data-testid="panorama-filters-more">
        <summary className="cursor-pointer text-xs font-medium">
          {t("modelIntel.panorama.filters.more")}
        </summary>
        <div className="mt-3 space-y-3">
          {!priceVisible && (
            <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="panorama-filter-price-forbidden">
              {t("modelIntel.panorama.filters.priceForbidden")}
            </p>
          )}
          <div className="grid gap-2 md:grid-cols-4">
            <NumberField
              label={t("modelIntel.panorama.filters.priceInMin")}
              testId="panorama-filter-price-in-min"
              value={filters.priceInputMin}
              disabled={!priceVisible}
              onChange={(next) => update({ priceInputMin: next })}
            />
            <NumberField
              label={t("modelIntel.panorama.filters.priceInMax")}
              testId="panorama-filter-price-in-max"
              value={filters.priceInputMax}
              disabled={!priceVisible}
              onChange={(next) => update({ priceInputMax: next })}
            />
            <NumberField
              label={t("modelIntel.panorama.filters.priceOutMin")}
              testId="panorama-filter-price-out-min"
              value={filters.priceOutputMin}
              disabled={!priceVisible}
              onChange={(next) => update({ priceOutputMin: next })}
            />
            <NumberField
              label={t("modelIntel.panorama.filters.priceOutMax")}
              testId="panorama-filter-price-out-max"
              value={filters.priceOutputMax}
              disabled={!priceVisible}
              onChange={(next) => update({ priceOutputMax: next })}
            />
          </div>

          <div className="grid gap-2 md:grid-cols-3">
            <NumberField
              label={t("modelIntel.panorama.filters.contextMin")}
              testId="panorama-filter-context-min"
              value={filters.contextMin}
              onChange={(next) => update({ contextMin: next })}
            />
            <label className="space-y-1 text-xs text-muted-foreground">
              <span className="block font-medium text-foreground">
                {t("modelIntel.panorama.filters.coverage")}
              </span>
              <select
                value={filters.minDomains}
                data-testid="panorama-filter-min-domains"
                onChange={(event) => update({ minDomains: Number(event.target.value) })}
                className="w-full rounded border bg-background px-2 py-1 text-xs text-foreground"
              >
                <option value={0}>{t("modelIntel.panorama.filters.any")}</option>
                {[1, 2, 3, 4, 5, 6].map((value) => (
                  <option key={value} value={value}>
                    {t("modelIntel.panorama.filters.coverageAtLeast", { count: value })}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-xs text-muted-foreground">
              <span className="block font-medium text-foreground">
                {t("modelIntel.panorama.filters.freshness")}
              </span>
              <select
                value={filters.publishedWithinDays ?? ""}
                data-testid="panorama-filter-freshness"
                onChange={(event) =>
                  update({
                    publishedWithinDays:
                      event.target.value === "" ? null : Number(event.target.value),
                  })
                }
                className="w-full rounded border bg-background px-2 py-1 text-xs text-foreground"
              >
                <option value="">{t("modelIntel.panorama.filters.any")}</option>
                <option value={30}>{t("modelIntel.panorama.filters.freshness30")}</option>
                <option value={90}>{t("modelIntel.panorama.filters.freshness90")}</option>
                <option value={180}>{t("modelIntel.panorama.filters.freshness180")}</option>
              </select>
            </label>
          </div>

          <fieldset>
            <legend className="text-xs font-medium">
              {t("modelIntel.panorama.filters.capabilities")}
            </legend>
            <div className="mt-1 grid grid-cols-2 gap-1 md:grid-cols-4">
              {PANORAMA_CAPABILITIES.map((capability) => (
                <label key={capability} className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={filters.capabilities.includes(capability)}
                    data-testid={`panorama-filter-cap-${capability}`}
                    onChange={() =>
                      update({
                        capabilities: toggleList(filters.capabilities, capability),
                      })
                    }
                  />
                  {capabilityLabel(capability)}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-xs font-medium">
              {t("modelIntel.panorama.filters.lifecycle")}
            </legend>
            <div className="mt-1 flex flex-wrap gap-3">
              {options.lifecycles.map((lifecycle) => (
                <label key={lifecycle.value} className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={filters.lifecycle.includes(lifecycle.value)}
                    data-testid={`panorama-filter-lifecycle-${lifecycle.value}`}
                    onChange={() =>
                      update({
                        lifecycle: toggleList(filters.lifecycle, lifecycle.value),
                      })
                    }
                  />
                  {lifecycleLabel(lifecycle.value)}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-2 md:grid-cols-3">
            <label className="space-y-1 text-xs text-muted-foreground">
              <span className="block font-medium text-foreground">
                {t("modelIntel.panorama.filters.openWeights")}
              </span>
              <select
                value={filters.openWeights}
                data-testid="panorama-filter-open-weights"
                onChange={(event) =>
                  update({
                    openWeights: event.target.value as PanoramaFilters["openWeights"],
                  })
                }
                className="w-full rounded border bg-background px-2 py-1 text-xs text-foreground"
              >
                <option value="any">{t("modelIntel.panorama.filters.any")}</option>
                <option value="open">{t("modelIntel.panorama.filters.openYes")}</option>
                <option value="proprietary">{t("modelIntel.panorama.filters.openNo")}</option>
              </select>
            </label>
            {options.licenses.length > 0 ? (
              <MultiSelect
                label={t("modelIntel.panorama.filters.license")}
                testId="panorama-filter-licenses"
                options={options.licenses}
                value={filters.licenses}
                onChange={(next) => update({ licenses: next })}
                size={3}
              />
            ) : (
              <div className="space-y-1 text-xs text-muted-foreground">
                <span className="block font-medium text-foreground">
                  {t("modelIntel.panorama.filters.license")}
                </span>
                <p data-testid="panorama-filter-license-empty">
                  {t("modelIntel.panorama.filters.licenseEmpty")}
                </p>
              </div>
            )}
            <label className="space-y-1 text-xs text-muted-foreground">
              <span className="block font-medium text-foreground">
                {t("modelIntel.panorama.filters.knowledgeCutoff")}
              </span>
              <input
                type="month"
                value={filters.knowledgeCutoffAfter ?? ""}
                data-testid="panorama-filter-cutoff"
                onChange={(event) =>
                  update({ knowledgeCutoffAfter: event.target.value || null })
                }
                className="w-full rounded border bg-background px-2 py-1 text-xs text-foreground"
              />
            </label>
          </div>

          <div className="grid gap-2 md:grid-cols-3">
            <NumberField
              label={t("modelIntel.panorama.filters.eloPerUsdMin")}
              testId="panorama-filter-elo-usd-min"
              value={filters.eloPerUsdMin}
              disabled={!priceVisible}
              onChange={(next) => update({ eloPerUsdMin: next })}
            />
            <NumberField
              label={t("modelIntel.panorama.filters.eloPerUsdMax")}
              testId="panorama-filter-elo-usd-max"
              value={filters.eloPerUsdMax}
              disabled={!priceVisible}
              onChange={(next) => update({ eloPerUsdMax: next })}
            />
            <p className="self-end text-[11px] text-muted-foreground">
              {t("modelIntel.panorama.filters.eloPerUsdHint")}
            </p>
          </div>

          <div className="grid gap-2 md:grid-cols-2">
            <label className="space-y-1 text-xs text-muted-foreground">
              <span className="block font-medium text-foreground">
                {t("modelIntel.panorama.filters.limit")}
              </span>
              <select
                value={filters.limit}
                data-testid="panorama-filter-limit"
                onChange={(event) => update({ limit: Number(event.target.value) })}
                className="w-full rounded border bg-background px-2 py-1 text-xs text-foreground"
              >
                <option value={10}>{t("modelIntel.panorama.filters.limit10")}</option>
                <option value={20}>{t("modelIntel.panorama.filters.limit20")}</option>
                <option value={50}>{t("modelIntel.panorama.filters.limit50")}</option>
                <option value={0}>{t("modelIntel.panorama.filters.limitAll")}</option>
              </select>
            </label>
            <label className="space-y-1 text-xs text-muted-foreground">
              <span className="block font-medium text-foreground">
                {t("modelIntel.panorama.filters.sort")}
              </span>
              <select
                value={filters.sort}
                data-testid="panorama-filter-sort"
                onChange={(event) =>
                  update({ sort: event.target.value as PanoramaFilters["sort"] })
                }
                className="w-full rounded border bg-background px-2 py-1 text-xs text-foreground"
              >
                <option value="elo_desc">{t("modelIntel.panorama.filters.sortEloDesc")}</option>
                <option value="elo_asc">{t("modelIntel.panorama.filters.sortEloAsc")}</option>
                <option value="value_desc">{t("modelIntel.panorama.filters.sortValueDesc")}</option>
                <option value="name">{t("modelIntel.panorama.filters.sortName")}</option>
              </select>
            </label>
          </div>
        </div>
      </details>

      {matchedModels === 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="panorama-filters-empty">
          {t("modelIntel.panorama.filters.empty")}
        </p>
      )}
        </div>
      )}
    </section>
  );
}
